// ================================================================
// 管理員日誌頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-26
// 功能：顯示管理員操作日誌，支援篩選和分頁
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Refresh,
  FilterList,
  ExpandMore,
  ExpandLess,
  History,
  Person,
  AccountBalance,
  Block,
  CheckCircle,
  Settings,
  Login,
} from '@mui/icons-material';
import {
  getAdminLogs,
  AdminLog,
  AdminLogFilters,
  AdminLogTypeStats,
  AdminListItem,
  Pagination,
} from '../../api/admin';
import AdminLayout from '../../components/admin/AdminLayout';

// 操作類型中文對應
const actionTypeLabels: { [key: string]: string } = {
  admin_login: '管理員登入',
  balance_adjust: '餘額調整',
  points_adjust: '點數調整',
  user_suspend: '停用帳號',
  user_activate: '啟用帳號',
  update_currency: '更新匯率',
  'flowtime-set': '設定 FlowTime',
  'flowtime-reset': '重設 FlowTime',
  'currency-rate-update': '更新匯率',
};

// 操作類型圖示
const getActionIcon = (actionType: string) => {
  switch (actionType) {
    case 'admin_login':
      return <Login fontSize="small" />;
    case 'balance_adjust':
    case 'points_adjust':
      return <AccountBalance fontSize="small" />;
    case 'user_suspend':
      return <Block fontSize="small" />;
    case 'user_activate':
      return <CheckCircle fontSize="small" />;
    case 'update_currency':
    case 'currency-rate-update':
      return <Settings fontSize="small" />;
    default:
      return <History fontSize="small" />;
  }
};

// 操作類型顏色
const getActionColor = (actionType: string): 'success' | 'error' | 'info' | 'warning' | 'default' => {
  switch (actionType) {
    case 'admin_login':
      return 'info';
    case 'balance_adjust':
    case 'points_adjust':
      return 'warning';
    case 'user_suspend':
      return 'error';
    case 'user_activate':
      return 'success';
    default:
      return 'default';
  }
};

// ================================================================
// 主要組件
// ================================================================

const AdminLogsPage: React.FC = () => {
  // 狀態
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [typeStats, setTypeStats] = useState<AdminLogTypeStats[]>([]);
  const [adminsList, setAdminsList] = useState<AdminListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 篩選條件（預設顯示）
  const [showFilters, setShowFilters] = useState(true);
  const [adminIdFilter, setAdminIdFilter] = useState<string>('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');

  // 載入日誌
  const loadLogs = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const filters: AdminLogFilters = {
        page,
        limit: pagination.page_size,
      };

      if (adminIdFilter) filters.admin_id = parseInt(adminIdFilter);
      if (actionTypeFilter) filters.action_type = actionTypeFilter;
      if (startDateFilter) filters.start_date = startDateFilter;
      if (endDateFilter) filters.end_date = endDateFilter;

      const response = await getAdminLogs(filters);

      if (response.success) {
        setLogs(response.logs || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
        if (response.type_stats) {
          setTypeStats(response.type_stats);
        }
        if (response.admins) {
          setAdminsList(response.admins);
        }
      } else {
        setError(response.message || '無法載入日誌');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page_size, adminIdFilter, actionTypeFilter, startDateFilter, endDateFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // 分頁處理
  const handleChangePage = (_: unknown, newPage: number) => {
    loadLogs(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({ ...prev, page_size: parseInt(event.target.value, 10) }));
    loadLogs(1);
  };

  // 清除篩選
  const handleClearFilters = () => {
    setAdminIdFilter('');
    setActionTypeFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  // 格式化詳情
  const formatDetails = (details: string | null): string => {
    if (!details) return '-';
    try {
      const parsed = JSON.parse(details);
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    } catch {
      return details;
    }
  };

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            管理員日誌
          </Typography>
          <Box>
            <Tooltip title="篩選">
              <IconButton onClick={() => setShowFilters(!showFilters)} sx={{ mr: 1 }}>
                <FilterList />
              </IconButton>
            </Tooltip>
            <Tooltip title="重新整理">
              <IconButton onClick={() => loadLogs(pagination.page)}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 統計卡片 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {typeStats.slice(0, 4).map((stat) => (
            <Grid item xs={6} md={3} key={stat.action_type}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ mb: 1 }}>{getActionIcon(stat.action_type)}</Box>
                  <Typography variant="h5" fontWeight="bold">
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {actionTypeLabels[stat.action_type] || stat.action_type}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 篩選區域 */}
        <Collapse in={showFilters}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              篩選條件
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>管理員</InputLabel>
                  <Select
                    value={adminIdFilter}
                    label="管理員"
                    onChange={(e) => setAdminIdFilter(e.target.value)}
                  >
                    <MenuItem value="">全部</MenuItem>
                    {adminsList.map((admin) => (
                      <MenuItem key={admin.admin_id} value={admin.admin_id.toString()}>
                        {admin.username} ({admin.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>操作類型</InputLabel>
                  <Select
                    value={actionTypeFilter}
                    label="操作類型"
                    onChange={(e) => setActionTypeFilter(e.target.value)}
                  >
                    <MenuItem value="">全部</MenuItem>
                    {typeStats.map((stat) => (
                      <MenuItem key={stat.action_type} value={stat.action_type}>
                        {actionTypeLabels[stat.action_type] || stat.action_type} ({stat.count})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="開始日期"
                  type="date"
                  value={startDateFilter}
                  onChange={(e) => setStartDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="結束日期"
                  type="date"
                  value={endDateFilter}
                  onChange={(e) => setEndDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Box display="flex" gap={1}>
                  <Button variant="contained" onClick={() => loadLogs(1)} fullWidth>
                    搜尋
                  </Button>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    清除
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* 日誌列表 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>時間</TableCell>
                  <TableCell>管理員</TableCell>
                  <TableCell>操作類型</TableCell>
                  <TableCell>目標使用者</TableCell>
                  <TableCell>詳細資訊</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">沒有找到日誌記錄</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.log_id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.timestamp).toLocaleString('zh-TW')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {log.admin_username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.admin_email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getActionIcon(log.action_type)}
                          label={actionTypeLabels[log.action_type] || log.action_type}
                          color={getActionColor(log.action_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {log.target_user_id ? (
                          <Box>
                            <Typography variant="body2">
                              {log.target_user_name || log.target_user_email || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {log.target_user_id}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={formatDetails(log.details)}
                        >
                          {formatDetails(log.details)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 分頁 */}
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={pagination.page_size}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="每頁顯示"
          />
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default AdminLogsPage;
